import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, FormControl, FormGroup, FormLabel, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import rest from "../../../common/rest";
import { refreshTemplateVersions } from "../../../ducks/survey";
import DatePickerComponent from "../../common/datePicker/DatePicker";
import IconTooltip from "../../common/IconTooltip";

export function ReportTemplateYearDialog({ template, onClose, refreshTemplateVersions }) {
  const [month, setMonth] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const { identifier, version, latestVersion } = template;
  const url = `/admin/admin/surveys/${identifier}/version/${version}`;

  useEffect(() => {
    let active = true;
    Promise.resolve(rest.doGet(url)).then((survey) => {
      if (!active) return;
      const date = survey.reportTemplateYear ?? survey.surveyTemplate?.reportTemplateYear;
      setMonth(date == null ? "" : new Date(date).toISOString().slice(0, 7));
      setLoaded(true);
    }).catch(() => { if (active) setError("template.year.loadError"); });
    return () => { active = false; };
  }, [url]);

  const save = async (includeFollowingVersions) => {
    if (month && !/^[1-9]\d{3}-(0[1-9]|1[0-2])$/.test(month)) {
      setInvalid(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await rest.doPut(`${url}/report-template-year`, JSON.stringify({
        reportTemplateYear: month ? `${month}-01T00:00:00.000Z` : null,
        includeFollowingVersions
      }));
      await refreshTemplateVersions(identifier);
      onClose();
    } catch {
      setError("template.year.saveError");
      setSaving(false);
    }
  };

  return (
    <Modal show size="lg" onHide={saving ? undefined : onClose}>
      <Modal.Header closeButton={!saving}>
        <Modal.Title><FormattedMessage id="template.year.edit" /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{template.surveyName} · <FormattedMessage id="templateList.version" /> {version}</p>
        {error && <Alert variant="danger"><FormattedMessage id={error} /></Alert>}
        <FormGroup controlId="reportTemplateYear">
          <FormLabel>
            <FormattedMessage id="user.surveyBuilder.reportTemplateYear" />
            <IconTooltip iconDefinition={faInfoCircle} iconClass="ms-2 text-info"
              tooltipMessageId="user.surveyBuilder.reportTemplateYear.tooltip" />
          </FormLabel>
          <DatePickerComponent name="reportTemplateYear" dateFormat="Month" displayFormat="YYYY-MM"
            value={month} disabled={!loaded || saving}
            onChange={(value) => {
              setMonth(typeof value === "string" ? value : value.format("YYYY-MM"));
              setInvalid(false);
            }}
            inputProps={{ id: "reportTemplateYear", "aria-invalid": invalid,
              className: `form-control${invalid ? " is-invalid" : ""}` }} />
          {invalid && <FormControl.Feedback type="invalid" className="d-block">
            <FormattedMessage id="survey.settings.reportTemplateYearInvalid" />
          </FormControl.Feedback>}
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={saving} onClick={onClose}><FormattedMessage id="app.common.Cancel" /></Button>
        <Button disabled={!loaded || saving} onClick={() => save(false)}>
          <FormattedMessage id="survey.settings.save.thisVersion" />
        </Button>
        {Number(version) < Number(latestVersion) && <Button disabled={!loaded || saving} onClick={() => save(true)}>
          <FormattedMessage id="survey.settings.save.followingVersions" />
        </Button>}
      </Modal.Footer>
    </Modal>
  );
}

ReportTemplateYearDialog.propTypes = {
  template: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  refreshTemplateVersions: PropTypes.func.isRequired
};

export default connect(null, { refreshTemplateVersions })(ReportTemplateYearDialog);
